import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import { Card, Button, Form, Input, Select, InputNumber, Table, Modal, Tag, Descriptions, message, Alert, Result } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { formatCurrency } from '../utils/formatters';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHODS } from '../models/types';
import storageService from '../services/storageService';

const PaymentPage = () => {
  const { invoices, payments, addPayment } = useApp();
  const { user } = useAuth();
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [form] = Form.useForm();
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const [printPayment, setPrintPayment] = useState(null);
  const [appName, setAppName] = useState('');
  const isViewer = user?.role === 'viewer';

  useEffect(() => {
    async function fetchAppName() {
      const name = await storageService.getSetting('app_name', '');
      setAppName(name);
    }
    fetchAppName();
  }, []);

  const handleDownloadPDF = () => {
    if (!printPayment) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(appName || "Nom de l'application", 10, 10);
    doc.setFontSize(12);
    doc.text(`REÇU DE PAIEMENT`, 10, 20);
    doc.text(`Date et heure: ${new Date(printPayment.paymentDate).toLocaleString('fr-FR')}`, 10, 30);
    doc.text(`Montant: ${formatCurrency(printPayment.amount)}`, 10, 40);
    doc.text(`Méthode: ${PAYMENT_METHOD_LABELS[printPayment.method]}`, 10, 50);
    doc.text(`Référence: ${printPayment.reference || 'N/A'}`, 10, 60);
    doc.save(`recu-paiement-${printPayment.id}.pdf`);
  };

  const unpaidInvoices = invoices.filter(i => i.remainingAmount > 0);

  const handleAddPayment = (invoice) => {
    setSelectedInvoice(invoice);
    form.setFieldsValue({
      invoiceId: invoice.id,
      subscriberId: invoice.subscriberId,
      amount: invoice.remainingAmount,
      method: PAYMENT_METHODS.CASH
    });
    setIsPaymentOpen(true);
  };

  const handlePaymentSubmit = async (values) => {
    try {
      if (!selectedInvoice) {
        message.error('Aucune facture sélectionnée');
        return;
      }

      const payment = {
        ...values,
        subscriberId: selectedInvoice.subscriberId,
        paymentDate: new Date().toISOString()
      };

      const result = await addPayment(payment, user?.id);
      
      if (!result) {
        message.error('Erreur lors de l\'enregistrement du paiement');
        return;
      }

      // Add to cash register if cash payment
      if (values.method === PAYMENT_METHODS.CASH) {
          // addCashTransaction supprimé car non défini dans le contexte
      }

      message.success('Paiement enregistré avec succès');
      setPrintPayment(result);
      setIsPrintOpen(true);
      setIsPaymentOpen(false);
      form.resetFields();
    } catch (error) {
      message.error('Erreur lors de l\'enregistrement');
    }
  };

  const paymentColumns = [
    {
      title: 'Date et heure',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => new Date(date).toLocaleString('fr-FR')
    },
    {
      title: 'Facture',
      dataIndex: 'invoiceId',
      key: 'invoiceId'
    },
    {
      title: 'Montant',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Méthode',
      dataIndex: 'method',
      key: 'method',
      render: (method) => <Tag>{PAYMENT_METHOD_LABELS[method]}</Tag>
    },
    {
      title: 'Ref',
      dataIndex: 'reference',
      key: 'reference'
    }
  ];

  const invoiceColumns = [
    {
      title: 'Numéro',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber'
    },
    {
      title: 'Abonné',
      dataIndex: 'subscriberName',
      key: 'subscriberName'
    },
    {
      title: 'Montant',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Reste à payer',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: (amount) => <span style={{ color: 'red' }}>{formatCurrency(amount)}</span>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => !isViewer && (
        <Button type="primary" size="small" onClick={() => handleAddPayment(record)}>
          Payer
        </Button>
      )
    }
  ];

  return (
    <div>
      <h1>Gestion des Paiements</h1>

      {isViewer && (
        <Alert
          message="Mode Consultation"
          description="Vous êtes en mode lecture seule. Vous ne pouvez pas enregistrer de paiements."
          type="info"
          showIcon
          style={{ marginBottom: 20 }}
        />
      )}

      <div style={{ marginBottom: 20 }}>
        <Card title={`Factures à payer (${unpaidInvoices.length})`}>
          <Table
            columns={invoiceColumns}
            dataSource={unpaidInvoices}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            size="small"
          />
        </Card>
      </div>

      <div>
        <Card title={`Historique des paiements (${payments.length})`}>
          <Table
            columns={paymentColumns}
            dataSource={payments}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </Card>
      </div>

      <Modal
        title="Enregistrer un paiement"
        open={isPaymentOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsPaymentOpen(false)}
        width={600}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePaymentSubmit}
        >
          <Form.Item
            label="ID Abonné"
            name="subscriberId"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="ID Facture"
            name="invoiceId"
          >
            <Input disabled />
          </Form.Item>

          <Form.Item
            label="Montant à payer (FCFA)"
            name="amount"
            rules={[{ required: true, message: 'Le montant est obligatoire' }]}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="Méthode de paiement"
            name="method"
            rules={[{ required: true, message: 'Sélectionner une méthode' }]}
          >
            <Select
              options={Object.entries(PAYMENT_METHODS).map(([key, value]) => ({
                label: PAYMENT_METHOD_LABELS[value],
                value: value
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Référence (Chèque, virement...)"
            name="reference"
          >
            <Input placeholder="Référence de transaction" />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea rows={2} placeholder="Notes additionnelles" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reçu de paiement"
        open={isPrintOpen}
        onCancel={() => setIsPrintOpen(false)}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
            Imprimer
          </Button>,
          <Button key="pdf" type="default" onClick={handleDownloadPDF}>
            Télécharger PDF
          </Button>,
          <Button key="close" onClick={() => setIsPrintOpen(false)}>
            Fermer
          </Button>
        ]}
        width={500}
      >
        {printPayment && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h2>REÇU DE PAIEMENT</h2>
            <Descriptions bordered column={1} size="small" style={{ marginTop: 20 }}>
              <Descriptions.Item label="Date et heure">
                {new Date(printPayment.paymentDate).toLocaleString('fr-FR')}
              </Descriptions.Item>
                          <div style={{ marginTop: 10, fontWeight: 'bold', fontSize: 16 }}>
                            {appName || 'Nom de l\'application'}
                          </div>
              <Descriptions.Item label="Montant">
                {formatCurrency(printPayment.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Méthode">
                {PAYMENT_METHOD_LABELS[printPayment.method]}
              </Descriptions.Item>
              <Descriptions.Item label="Référence">
                {printPayment.reference || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 30 }}>
              <QRCodeSVG
                value={JSON.stringify({
                  paymentId: printPayment.id,
                  amount: printPayment.amount,
                  date: printPayment.paymentDate
                })}
                size={200}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentPage;
